// src/components/FAQ.jsx
import React, { useState, useRef, useEffect } from 'react';
import arrowUp from '../assets/arrow_up.svg';
import arrowDown from '../assets/arrow_down.svg';
import faqVideoLinkLocal from '../assets/FAQ-video-link.mp4';
import './FAQ.css';
import { useTranslation, Trans } from 'react-i18next';


const renderInline = (text) => (
  <Trans defaults={text} components={{ highlight : <b></b>}} />
);

const splitColumns = (line) => {
  if (line.includes('\t'))  return line.split('\t');
  if (line.includes('\\t')) return line.split('\\t');
  const spaced = line.split(/\s{2,}/); // 2+ пробелов
  return spaced.length > 1 ? spaced : [line];
};

const renderTable = (text) => {
  const lines = String(text).replace(/\r/g,'').split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;

  const headers = splitColumns(lines[0]).map(c => c.trim());
  const rows    = lines.slice(1).map(r => splitColumns(r).map(c => c.trim()));
  const cols    = Math.max(headers.length, ...rows.map(r => r.length));
  if (cols < 2) return null;

  const pad = (arr,n) => arr.concat(Array(Math.max(0, n - arr.length)).fill(''));
  const H = pad(headers, cols);
  const R = rows.map(r => pad(r, cols));

  return (
    <table style={{ borderCollapse:'collapse', width:'100%' }}>
      <thead>
        <tr>
          {H.map((h,i)=>(
            <th key={i} style={{ border:'1px solid #000', padding:8, textAlign:'left' }}>
              {renderInline(h)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {R.map((r,ri)=>(
          <tr key={ri}>
            {r.map((c,ci)=>(
              <td key={ci} style={{ border:'1px solid #000', padding:8 }}>
                {renderInline(c)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};


// === Парсер абзацев и списков (-, *, •) ===
// 1) isBullet — теперь терпит отсутствие пробела после «•»
const parseBlocks = (text) => {
  const isBullet = (l) => /^\s*([\-*•])(?:\s+|$)/.test(l); // <-- было \s+; стало (?:\s+|$)
  const lines = String(text).split('\n');

  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trimEnd();

    if (!line.trim()) { i++; continue; }

    if (isBullet(line)) {
      const items = [];
      while (i < lines.length && isBullet(lines[i])) {
        items.push(lines[i].replace(/^\s*([\-*•])(?:\s+|$)/, '').trim());
        i++;
      }
      blocks.push(items.length >= 2 ? { type: 'ul', items } : { type: 'p', lines: items });
      continue;
    }

    const paraLines = [line.trim()];
    i++;
    while (i < lines.length && lines[i].trim() !== '' && !isBullet(lines[i])) {
      paraLines.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: 'p', lines: paraLines });
  }
  return blocks;
};

// Инлайн-буллиты: режем на первой \n ПОСЛЕ первой «•», хвост забираем целиком
const extractInlineBullets = (raw) => {
  if (!raw || raw.indexOf('•') === -1) return null;
  const s = String(raw).replace(/\r/g, '');

  const first = s.indexOf('•');
  const nl    = s.indexOf('\n', first);       // <-- ищем \n ПОСЛЕ первой «•»

  const head = s.slice(0, first).trimEnd();   // вводный текст
  const listChunk = nl === -1 ? s.slice(first) : s.slice(first, nl);
  const items = listChunk.split('•').map(x => x.trim()).filter(Boolean);

  if (items.length < 2) return null;          // минимум 2 пункта — иначе не список

  const after = nl === -1 ? '' : s.slice(nl + 1); // весь хвост после \n (м.б. много строк)
  return { head, items, after };
};




// 3) Учёт inline-буллитов в рендере + сохранение \n через <br/>
const renderRichText = (text) => {
  if (text == null) return null;
  const s = String(text);

  // Таблица по \t — без изменений
  if (s.includes('\t')) return renderTable(s);

  // Инлайн-буллиты: «Да:•п1•п2•п3»
  const inline = extractInlineBullets(s);
  if (inline) {
    return (
      <div>
        {inline.head && (
          <p>
            {inline.head.split('\n').map((ln, i) => (
              <React.Fragment key={i}>
                {renderInline(ln)}
                {i < inline.head.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        )}
        <ul className="faq-ul">
          {inline.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
        {inline.after && (
          <p>
            {inline.after.split('\n').map((ln, i) => (
              <React.Fragment key={i}>
                {renderInline(ln)}
                {i < inline.after.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        )}
      </div>
    );
  }

  // Обычный разбор: абзацы + построчные списки (-, *, •) с переносами
  const blocks = parseBlocks(s);
  return (
    <div>
      {blocks.map((b, idx) => {
        if (b.type === 'ul') {
          return (
            <ul key={`ul-${idx}`} className="faq-ul">
              {b.items.map((it, j) => <li key={`li-${idx}-${j}`}>{renderInline(it)}</li>)}
            </ul>
          );
        }
        return (
          <p key={`p-${idx}`}>
            {b.lines.map((ln, k) => (
              <React.Fragment key={k}>
                {renderInline(ln)}
                {k < b.lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};



const Content = ({ textKey }) => {
  const { t } = useTranslation();
  return renderRichText(t(textKey));
};

export default function FAQ() {
  const { t } = useTranslation();

  // ссылка на видео берётся из переводов, иначе локальный mp4
  const videoLink = t('$Video_link2', { defaultValue: faqVideoLinkLocal }); // :contentReference[oaicite:3]{index=3}

  const items = [
    { title: t('$FAQ_1'),  content: <Content textKey="$FAQ_text_1" /> },
    { title: t('$FAQ_2'),  content: <Content textKey="$FAQ_text_2" /> },
    { title: t('$FAQ_3'),  content: <Content textKey="$FAQ_text_3" /> },
    { title: t('$FAQ_4'),  content: <Content textKey="$FAQ_text_4" /> }, // табличный блок с \t в переводе :contentReference[oaicite:4]{index=4}
    { title: t('$FAQ_5'),  content: <Content textKey="$FAQ_text_5" /> },
    { title: t('$FAQ_6'),  content: <Content textKey="$FAQ_text_6" /> },
    { title: t('$FAQ_7'),  content: <Content textKey="$FAQ_text_7" /> },
    { title: t('$FAQ_8'),  content: <Content textKey="$FAQ_text_8" /> },
    { title: t('$FAQ_9'),  content: <Content textKey="$FAQ_text_9" /> },
    {
      title: t('$FAQ_10'),
      content: (
        <p>
          <Trans
            defaults={t('$FAQ_text_10').replace('demoVideo', '<a>demoVideo</a>')}
            components={{ a: <a href={videoLink} target="_blank" rel="noopener noreferrer" /> }}
          />
        </p>
      ),
    },
  ];

  return (
    <div className="faq">
      <h1>{t('$Header7')}</h1> {/* заголовок из переводов */} {/* :contentReference[oaicite:5]{index=5} */}
      <div className="accordion">
        {items.map((item, index) => (
          <AccordionItem key={index} item={item} />
        ))}
      </div>
    </div>
  );
}

function AccordionItem({ item }) {
  const { title, content } = item;
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState('0px');

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(isOpen ? `${contentRef.current.scrollHeight}px` : '0px');
    }
  }, [isOpen]);

  return (
    <div className="accordion-item">
      <button
        className={`accordion-header ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(prev => !prev)}
      >
        {title}
        <span className="accordion-icon">{isOpen ? <img src={arrowUp} alt="" /> : <img src={arrowDown} alt="" />}</span>
      </button>
      <div ref={contentRef} className="accordion-content" style={{ maxHeight }}>
        <div className="accordion-content-text">{content}</div>
      </div>
    </div>
  );
}
